import type { LiveSessionTicket } from "@/lib/api/live-api";

import { sendSessionSignature, sendSessionTempo } from "./session-control";
import type { LiveSessionState } from "./session-state";
import { proposedBpm, sameSignature, sessionBpm } from "./session-tempo";

/**
 * La grille que l'interprète avait en entrant dans la séance : le tempo
 * affiché et le chiffrage du métronome, avant que la séance ne pose les siens.
 */
export interface EntryGrid {
  bpm: number;
  signature: { num: number; den: number };
}

/** Les deux commandes, remplaçables pour les essais. */
export interface EntryGridSenders {
  tempo: typeof sendSessionTempo;
  signature: typeof sendSessionSignature;
}

const SENDERS: EntryGridSenders = { tempo: sendSessionTempo, signature: sendSessionSignature };

/**
 * Donne à une séance neuve le tempo et le chiffrage que l'interprète avait en
 * entrant.
 *
 * # Pourquoi
 *
 * Une séance naît à 120 en 4/4, et sa grille remplace celle du studio dès
 * qu'on y entre. Qui réglait 80 en 6/8 puis cliquait « Enregistrer »
 * enregistrait donc à 120 en 4/4, ou devait tout régler une seconde fois dans
 * la salle. Constaté le 2026-09-21 : le tempo reposé à la main, le chiffrage
 * jamais, et un replay en 4/4.
 *
 * # Quand
 *
 * À la mise en marche, avant la reprise, par l'interprète seul, et seulement
 * sur une séance que personne n'a réglée : en pause à son point de départ, sans
 * marque de tempo. Rejoindre une séance lancée ne lui impose rien — sa grille
 * fait foi, et les notes déjà jouées y sont datées.
 *
 * Les commandes partent l'une après l'autre, et la reprise qui suit les trouve
 * appliquées. Un échec n'arrête rien : la séance démarre sur sa grille à elle,
 * qu'on règle ensuite à la main.
 */
export async function carryEntryGrid(
  ticket: Pick<LiveSessionTicket, "role" | "clockUrl" | "room" | "token">,
  state: LiveSessionState,
  entry: EntryGrid,
  atMs: number,
  send: EntryGridSenders = SENDERS,
): Promise<void> {
  if (ticket.role !== "performer" || !state.paused || state.positionMs !== 0 || state.tempoMarks.length > 0) {
    return;
  }

  const bpm = proposedBpm(entry.bpm);

  // Le tick de la position zéro est zéro, quelle que soit la grille.
  if (bpm !== proposedBpm(sessionBpm(state.tempoBpm, state.tempoMarks))) {
    await send.tempo(ticket.clockUrl, ticket.room, ticket.token, { bpm, tick: 0 }, 0, atMs);
  }

  const signature = { beats: entry.signature.num, unit: entry.signature.den };

  if (!sameSignature(signature, state.signature)) {
    await send.signature(ticket.clockUrl, ticket.room, ticket.token, signature, 0, atMs);
  }
}
